import React, { useEffect, useState, useCallback } from 'react'
import { GetServiceParactiseaddress } from '../../../api/helper';
import { useSelector } from "react-redux";

function WorkingHours({ 
    workingHoursData = [], 
    onScheduleChange,
    onSave 
}) {
    const profiledata = useSelector((state) => state?.cookies?.userDetails);
    const [servicelocation, setServiceLocation] = useState([]);
    const [weeklySchedule, setWeeklySchedule] = useState({
        Monday: [], Tuesday: [], Wednesday: [], Thursday: [], Friday: [], Saturday: [], Sunday: [],
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const addTimeBlock = useCallback((day) => {
        const newBlock = { 
            start: "08:00", 
            end: "17:00", 
            location: servicelocation.length > 0 ? servicelocation[0]._id : "",
            _id: null,
            dayId: null
        };
        
        setWeeklySchedule((prevState) => {
            const updatedSchedule = {
                ...prevState,
                [day]: [...prevState[day], newBlock],
            };
            
            if (onScheduleChange) {
                onScheduleChange(prepareScheduleForSaving(updatedSchedule));
            }
            
            return updatedSchedule;
        });
    }, [servicelocation, onScheduleChange]);

    const removeTimeBlock = useCallback((day, index) => {
        setWeeklySchedule((prevState) => {
            const newDaySchedule = prevState[day].filter((_, i) => i !== index);
            const updatedSchedule = {
                ...prevState,
                [day]: newDaySchedule,
            };
            
            if (onScheduleChange) {
                onScheduleChange(prepareScheduleForSaving(updatedSchedule));
            }
            return updatedSchedule;
        });
    }, [onScheduleChange]);

    const fetchServiceLocation = async () => {
        if (!profiledata?._id) {
            setError("Profile data not available");
            return;
        }

        setLoading(true);
        setError(null);
        
        try {
            const response = await GetServiceParactiseaddress(profiledata._id);
            const locations = response?.data?.data?.data;
            
            if (Array.isArray(locations)) {
                setServiceLocation(locations);
            } else {
                setServiceLocation([]);
                setError("Invalid service location data format");
            }
        } catch (error) {
            console.error("Failed to fetch service locations:", error);
            setError("Failed to load service locations");
            setServiceLocation([]);
        } finally {
            setLoading(false);
        }
    };

    // Initialize schedule from props
    useEffect(() => {
        if (!Array.isArray(workingHoursData)) return;

        const schedule = {
            Monday: [],
            Tuesday: [],
            Wednesday: [],
            Thursday: [],
            Friday: [],
            Saturday: [],
            Sunday: [],
        };

        workingHoursData.forEach((entry) => {
            const day = entry.day;
            if (schedule.hasOwnProperty(day) && Array.isArray(entry.shifts)) {
                schedule[day] = entry.shifts.map((shift) => ({
                    start: shift.startTime || "08:00",
                    end: shift.endTime || "17:00",
                    location: shift.location || "",
                    _id: shift._id || null,
                    dayId: entry._id || null,
                }));
            }
        });

        setWeeklySchedule(schedule);
    }, [workingHoursData]);

    // Fetch locations on component mount
    useEffect(() => {
        fetchServiceLocation();
    }, [profiledata?._id]);

    // Prepare data for saving/returning - make it work with any schedule object
    const prepareScheduleForSaving = useCallback((scheduleData = weeklySchedule) => {
        const result = [];

        Object.entries(scheduleData).forEach(([day, shifts]) => {
            if (Array.isArray(shifts) && shifts.length > 0) {
                const validShifts = shifts.filter(shift => 
                    shift.start && 
                    shift.end && 
                    shift.start !== shift.end
                );
                
                if (validShifts.length > 0) {
                    result.push({
                        day,
                        _id: shifts[0]?.dayId || null, // Use dayId from first shift if available
                        shifts: validShifts.map((block) => ({
                            _id: block._id || null,
                            startTime: block.start,
                            endTime: block.end,
                            location: block.location,
                        })),
                    });
                }
            }
        });

        return result;
    }, [weeklySchedule]);

    const validateTimeBlock = useCallback((day, index, field, value, currentSchedule) => {
        const timeToMinutes = (time) => {
            if (!time || typeof time !== 'string') return 0;
            const [hours, minutes] = time.split(":").map(Number);
            if (isNaN(hours) || isNaN(minutes)) return 0;
            return hours * 60 + minutes;
        };

        const daySchedule = [...currentSchedule[day]];
        const currentBlock = { ...daySchedule[index], [field]: value };

        const currentStart = timeToMinutes(currentBlock.start);
        const currentEnd = timeToMinutes(currentBlock.end);

        // Validate start/end time logic
        if (currentEnd <= currentStart) {
            return { isValid: false, message: "End time must be after start time." };
        }

        // Check for overlaps with other blocks
        const isOverlapping = daySchedule.some((block, i) => {
            if (i === index) return false; // skip current block
            
            const blockStart = timeToMinutes(block.start);
            const blockEnd = timeToMinutes(block.end);

            // Check overlap conditions
            return (
                (currentStart >= blockStart && currentStart < blockEnd) || // starts within another block
                (currentEnd > blockStart && currentEnd <= blockEnd) ||     // ends within another block
                (currentStart <= blockStart && currentEnd >= blockEnd)     // fully contains another block
            );
        });

        if (isOverlapping) {
            return { isValid: false, message: "This time block overlaps with another on the same day." };
        }

        return { isValid: true, message: "" };
    }, []);

    const handleTimeBlockChange = useCallback((day, index, field, value) => {
        setWeeklySchedule((prevState) => {
            const validation = validateTimeBlock(day, index, field, value, prevState);
            
            if (!validation.isValid) {
                alert(validation.message);
                return prevState; // Reject update
            }

            // Create updated schedule
            const newDaySchedule = [...prevState[day]];
            newDaySchedule[index] = { ...newDaySchedule[index], [field]: value };

            const updatedSchedule = {
                ...prevState,
                [day]: newDaySchedule,
            };

            // Return updated data to parent component
            if (onScheduleChange) {
                onScheduleChange(prepareScheduleForSaving(updatedSchedule));
            }

            return updatedSchedule;
        });
    }, [validateTimeBlock, onScheduleChange, prepareScheduleForSaving]);

    const handleSave = async () => {
        const payload = prepareScheduleForSaving();
        console.log(payload,"payload=-=-=")
        if (payload.length === 0) {
            alert("Please add at least one working hour block before saving.");
            return;
        }

        console.log('Saving payload:', payload);
        
        try {
            if (onSave) {
                await onSave(payload);
            }
            alert("Working hours saved successfully.");
        } catch (error) {
            console.error("Failed to save working hours:", error);
            alert("Failed to save working hours. Please try again.");
        }
    };

    // Return current schedule data (can be called by parent via ref)
    const getCurrentSchedule = useCallback(() => {
        return prepareScheduleForSaving();
    }, [prepareScheduleForSaving]);

    // Expose methods to parent component via ref
    React.useImperativeHandle(React.forwardRef().ref, () => ({
        getCurrentSchedule,
        handleSave
    }));

    if (loading) {
        return <div className="settings-container_CalendarView">Loading service locations...</div>;
    }

    return (
        <div className="settings-container_CalendarView">
            <h2>Working Hours Settings</h2>
            <p>Configure your working hours, locations .</p>
            
            {error && (
                <div style={{ color: 'red', marginBottom: '10px', padding: '10px', border: '1px solid red', borderRadius: '4px' }}>
                    Error: {error}
                </div>
            )}
            
            <div style={{ marginBottom: 20 }}>
                {Object.keys(weeklySchedule).map((day) => (
                    <div key={day} className="day-row_CalendarView">
                        <div className="day-row-header_CalendarView">
                            <strong>{day}</strong>
                            <button
                                type="button"
                                className="add-block-btn_CalendarView"
                                onClick={() => addTimeBlock(day)}
                                disabled={loading}
                            >
                                Add Time Block
                            </button>
                        </div>
                        <div className="time-blocks_CalendarView">
                            {weeklySchedule[day].map((block, index) => (
                                <div key={`${day}-${index}`} className="time-block_CalendarView">
                                    <label>Start:</label>
                                    <input
                                        type="time"
                                        value={block.start || "08:00"}
                                        onChange={(e) =>
                                            handleTimeBlockChange(
                                                day,
                                                index,
                                                "start",
                                                e.target.value
                                            )
                                        }
                                        disabled={loading}
                                    />
                                    <label>End:</label>
                                    <input
                                        type="time"
                                        value={block.end || "17:00"}
                                        onChange={(e) =>
                                            handleTimeBlockChange(
                                                day,
                                                index,
                                                "end",
                                                e.target.value
                                            )
                                        }
                                        disabled={loading}
                                    />
                                    <label>Location:</label>
                                    <select
                                        value={block.location || ""}
                                        onChange={(e) =>
                                            handleTimeBlockChange(
                                                day,
                                                index,
                                                "location",
                                                e.target.value
                                            )
                                        }
                                        disabled={loading}
                                    >
                                        <option value="">-- Select Location --</option>
                                        {servicelocation.map((item) => (
                                            <option key={item._id} value={item._id}>
                                                {item?.name || 'Unnamed Location'}
                                            </option>
                                        ))}
                                    </select>
                                    <button
                                        type="button"
                                        className="remove-block-btn_CalendarView"
                                        onClick={() => removeTimeBlock(day, index)}
                                        disabled={loading}
                                    >
                                        Remove
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

export default WorkingHours;